import { Component, Inject, Input, PLATFORM_ID } from '@angular/core';
import { SharedService } from '../../../services/shared/shared.service';
import { HeroComponent } from '../../shared/hero/hero.component';
import { UserDto } from '../../../models/user-dto';
import { AuthServiceService } from '../../../services/authService/auth-service.service';
import { FirestoreService } from '../../../services/firestore/firestore.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { NotificationsComponent } from '../../admin/components/notifications/notifications.component';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ApplicationService } from '../../../services/application/application.service';
import { ApplicationResponse } from '../../../models/applicationResponse';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NotificationService } from '../../../services/notification/notification.service';
import { NotificationRequest } from '../../../models/notification-request';
import { Timestamp } from '@angular/fire/firestore';
import { switchMap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { EmailService } from '../../../services/email/email.service';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [HeroComponent, NotificationsComponent, FormsModule, CommonModule, RouterModule],
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.css']
})
export class UserProfileComponent {
  userUid: any;
  applications: ApplicationResponse[] = [];
  isLoggedIn = false;
  userInfo: UserDto = {
    id: '',
    email: '',
    fullName: '',
    image: ''
  };
  originalImage: string | null = ''; // Store original image URL
  tempImage: File | null = null; // Store selected file temporarily
  tempImageUrl: string | null = null; // Store temporary image URL for preview
  selectedApplication: ApplicationResponse = {} as ApplicationResponse; // For modal
  tempCvFile: File | null = null; // Store temporary CV file
  adminIds: string[] = []; // Store admin IDs
  activeTab: string = 'notifications'; // Default to notifications
  adminEmails: any;
  @Input() showHero: boolean = true;
  showSpinner = true;
  constructor(
    public sharedService: SharedService,
    private authService: AuthServiceService,
    private firestoreService: FirestoreService,
    private spinner: NgxSpinnerService,
    private applicationService: ApplicationService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private emailService: EmailService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.isUserLoggedIn();
    this.route.queryParams.subscribe(params => {
      const tab = params['tab'];
      this.activeTab = tab && ['account', 'notifications', 'applications'].includes(tab)
        ? tab
        : 'account';
    });

    this.firestoreService.getAdminsEmail().subscribe({
      next: res => this.adminEmails = res
    });
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.router.navigate(['/Profile'], { queryParams: { tab } }); // Update URL
  }

  isUserLoggedIn() {
    this.authService.isUserLoggedIn().pipe(
      switchMap(res => {
        this.isLoggedIn = res;
        if (this.isLoggedIn) {
          this.userUid = this.authService.getCurrentUserUid();
          return this.firestoreService.getById('users', this.userUid);
        }
        this.router.navigateByUrl("/Home");
        return [];
      }),
      switchMap(res => {
        if (res) {
          this.userInfo = res;
          this.originalImage = res.image || null;
          return this.applicationService.getApplicationsByUser(this.userUid);
        }
        return [];
      }),
      switchMap(res => {
        if (res) {
          this.applications = res;
          console.log(this.applications);
          return this.firestoreService.getAdminUserIds();
        }
        return [];
      })
    ).subscribe({
      next: adminIds => {
        if (adminIds) {
          this.adminIds = adminIds;
        }
        this.spinner.hide();
      },
      error: err => {
        this.sharedService.handleError(err);
        this.spinner.hide();
      }
    });
  }

  onFileSelected(event: any) {
    const file: File = event.target.files[0];
    if (file) {
      this.tempImage = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.tempImageUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  updateImage(url: string | null) {
    this.firestoreService.update('users', this.userUid, { image: url }).subscribe({
      next: () => {
        this.sharedService.handleSuccess('Bild erfolgreich aktualisiert');
        this.spinner.hide();
      },
      error: err => {
        this.sharedService.handleError(err);
        this.spinner.hide();
      }
    });
  }

  onResetImage() {
    this.tempImage = null;
    this.tempImageUrl = null;
    this.userInfo.image = this.originalImage || "";
  }

  onUpdate(form: NgForm) {
    if (form.invalid) {
      return;
    }
    this.spinner.show();
    const data = {
      fullName: this.userInfo.fullName,
      email: this.userInfo.email,
      job: this.userInfo.job,
      phoneCode: this.userInfo.phoneCode,
      phoneNumber: this.userInfo.phoneNumber,
      address: this.userInfo.address,
      country: this.userInfo.country,
      image: this.userInfo.image
    };

    if (this.tempImage) {
      const path = `users/${this.userUid}/avatar/${this.tempImage.name}`;
      this.firestoreService.uploadFile(path, this.tempImage).subscribe({
        next: url => {
          data.image = url;
          this.userInfo.image = url;
          this.originalImage = url;
          this.tempImage = null;
          this.tempImageUrl = null;
          this.updateProfile(data);
        },
        error: err => {
          this.sharedService.handleError(err);
          this.spinner.hide();
        }
      });
    } else {
      this.updateProfile(data);
    }
  }

  updateProfile(data: any) {
    this.firestoreService.update('users', this.userUid, data).subscribe({
      next: () => {
        this.sharedService.handleSuccess('Profil erfolgreich aktualisiert');
        this.spinner.hide();
      },
      error: err => {
        this.sharedService.handleError(err);
        this.spinner.hide();
      }
    });
  }

  getApplication() {
    this.applicationService.getApplicationsByUser(this.userUid).subscribe({
      next: res => {
        this.applications = res;
        this.showSpinner = false;
      },
      error: err => {
        this.sharedService.handleError(err, "", "Bewerbungen konnten nicht abgerufen werden");
        this.showSpinner = false;
      }
    });
  }

  openUpdateModal(app: ApplicationResponse) {
    this.selectedApplication = { ...app }; // Clone to avoid direct mutation
    this.tempCvFile = null; // Reset temp CV file
  }

  onCvSelected(event: any) {
    const file: File = event.target.files[0];
    if (file && file.type === 'application/pdf' && file.size <= 2 * 1024 * 1024) {
      this.tempCvFile = file;
    } else {
      this.sharedService.handleError('Bitte laden Sie eine gültige PDF-Datei hoch (max. 2 MB).');
    }
  }

  onUpdateApplication(form: NgForm) {
    if (form.invalid) {
      return;
    }
    this.spinner.show();
    const data: Partial<ApplicationResponse> = {
      coverLetter: this.selectedApplication.coverLetter,
      status: 'pending'
    };

    if (this.tempCvFile) {
      const path = `applications/${this.selectedApplication.id}/cv/${this.tempCvFile.name}`;
      this.firestoreService.uploadFile(path, this.tempCvFile).subscribe({
        next: url => {
          data.cvUrl = url;
          this.updateApplicationData(data);
        },
        error: err => {
          this.sharedService.handleError(err);
          this.spinner.hide();
        }
      });
    } else {
      this.updateApplicationData(data);
    }
  }

  updateApplicationData(data: Partial<ApplicationResponse>) {
    this.firestoreService.update('applications', this.selectedApplication.id ?? '', data).subscribe({
      next: () => {
        const request = new NotificationRequest();
        request.title = 'Bewerbung aktualisiert';
        request.body = `Benutzer ${this.userUid} hat die Bewerbung ${this.selectedApplication.id} für die Stelle ${this.selectedApplication.job.title} aktualisiert.`;
        request.status = 'UNREAD';
        request.type = 'APPLICATION_STATUS_UPDATE';
        request.userIds = this.adminIds;
        request.timestamp = Timestamp.now();
        request.isOpen = false;
        request.isNew = true;
        this.sendNotification(request);
        let emailRequest = {
          to_name: "Towards Germany Admin",
          reply_to: this.adminEmails,
          subject: request.title,
          body: request.body
        };

        this.emailService.sendEmail(emailRequest);
        this.sharedService.handleSuccess('Bewerbung erfolgreich aktualisiert');
        this.getApplication(); // Refresh applications
        this.spinner.hide();
      },
      error: err => {
        this.sharedService.handleError(err);
        this.spinner.hide();
      }
    });
  }

  confirmDelete(applicationId: string) {
    Swal.fire({
      title: 'Sind Sie sicher?',
      text: 'Möchten Sie diese Bewerbung löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ja, löschen!',
      cancelButtonText: 'Abbrechen'
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteApplication(applicationId);
      }
    });
  }

  deleteApplication(applicationId: string) {
    this.spinner.show();
    this.firestoreService.delete('applications', applicationId).subscribe({
      next: () => {
        // Remove from local list immediately
        this.applications = this.applications.filter(app => app.id !== applicationId);

        const request = new NotificationRequest();
        request.title = 'Bewerbung gelöscht';
        request.body = `Benutzer ${this.userUid} hat die Bewerbung ${applicationId} gelöscht.`;
        request.status = 'UNREAD';
        request.type = 'APPLICATION_DELETED';
        request.userIds = this.adminIds;
        request.timestamp = Timestamp.now();
        request.isOpen = false;
        request.isNew = true;
        this.sendNotification(request);

        let emailRequest = {
          to_name: "Towards Germany Admin",
          reply_to: this.adminEmails,
          subject: request.title,
          body: request.body
        };

        this.emailService.sendEmail(emailRequest);

        this.sharedService.handleSuccess('Bewerbung erfolgreich gelöscht');
        this.spinner.hide();
      },
      error: err => {
        this.sharedService.handleError(err);
        this.spinner.hide();
      }
    });
  }

  sendNotification(request: NotificationRequest) {
    console.log('Benachrichtigung wird gesendet:', request);
    this.notificationService.saveNotification(request).subscribe({
      next: () => console.log('Benachrichtigung an Admins gesendet'),
      error: err => {
        console.log('Benachrichtigungsfehler:', err);
        this.sharedService.handleError(err);
      }
    });
  }

  private loadAdminIds() {
    console.log("Admin-IDs abrufen");
    this.firestoreService.getAdminUserIds().subscribe({
      next: adminIds => {
        this.adminIds = adminIds;
        console.log("Admin-IDs sind:", this.adminIds);
      },
      error: err => this.sharedService.handleError(err, "", "Admin-IDs konnten nicht geladen werden")
    });
  }
}